import { GetIssuesStatisticsResponseDto } from '@issuemanager/api-client';
import { IndicatorProps } from '@issuemanager/apps/client/components';
import { useAtlasSemanticThemeTokens } from '@issuemanager/apps/client/theme';
import { TFunction } from 'i18next';

type StatisticsKeys = Exclude<keyof GetIssuesStatisticsResponseDto, 'total'>;

export function mapDataToIndicators(
  statistics: GetIssuesStatisticsResponseDto,
  t: TFunction<('common' | 'issue')[], undefined>,
  semantic: ReturnType<typeof useAtlasSemanticThemeTokens>,
): Record<StatisticsKeys, IndicatorProps[]> {
  if (!statistics) {
    return {} as Record<StatisticsKeys, IndicatorProps[]>;
  }

  const { total, status = {}, severity = {} } = statistics;
  const statusIndicators = [
    {
      title: t('issue:statistics.status.all'),
      value: total,
    },
    ...Object.entries(status).map(([key, value]) => ({
      title: (t as (key: string) => string)(`issue:statistics.status.${key}`),
      value,
    })),
  ];

  const severityIndicators = Object.entries(severity).map(([key, value]) => ({
    title: (t as (key: string) => string)(
      key === 'undefined'
        ? 'issue:statistics.severity.notSet'
        : `common:severity.${key}`,
    ),
    value,
    color: getColor(key, semantic),
  }));

  return {
    status: statusIndicators,
    severity: severityIndicators,
  };
}

const getColor = (
  key: string,
  semantic: ReturnType<typeof useAtlasSemanticThemeTokens>,
) => {
  switch (key) {
    case 'veryHigh':
    case 'critical':
      return '#ED8584';
    case 'high':
      return semantic.color.accent['03'].value;
    case 'medium':
      return semantic.color.accent['04'].value;
    case 'low':
      return semantic.color.accent['02'].value;
    case 'veryLow':
      return '#88BBA9';
    default:
      return semantic.color.action.disabled.value;
  }
};
